import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "../data/site";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.hash]);

    const linkClass = ({ isActive }, to) => {
        const active = to === "/" ? isActive && !location.hash : isActive;
        return `nav-link text-sm font-light tracking-wide transition-colors duration-300 ${
            active ? "active text-petrol" : "text-ink hover:text-petrol"
        }`;
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-line/70 bg-white/90 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 md:px-12">
                <Link to="/" aria-label="Home — Studio Dentistico Dr. Mario Massironi">
                    <Logo compact />
                </Link>

                <nav
                    className="hidden items-center gap-8 lg:flex"
                    aria-label="Navigazione principale"
                >
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testid}
                            className={(s) => linkClass(s, l.to)}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contatti"
                        data-testid="header-cta-prenota"
                        className="inline-flex items-center gap-2 rounded-full bg-petrol px-6 py-2.5 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                    >
                        <CalendarCheck className="h-4 w-4" />
                        Prenota una visita
                    </Link>
                </nav>

                <button
                    type="button"
                    data-testid="mobile-menu-button"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Chiudi menu" : "Apri menu"}
                    className="rounded-full border border-line p-2.5 text-ink transition-colors hover:border-petrol hover:text-petrol lg:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
                        className="overflow-hidden border-b border-line bg-white/95 backdrop-blur-xl lg:hidden"
                        aria-label="Menu mobile"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.3 }}
                                >
                                    <Link
                                        to={l.to}
                                        data-testid={`${l.testid}-mobile`}
                                        className="block py-3 font-serif text-2xl font-normal text-ink transition-colors hover:text-petrol"
                                    >
                                        {l.label}
                                    </Link>
                                </motion.div>
                            ))}
                            <Link
                                to="/contatti"
                                data-testid="mobile-cta-prenota"
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-petrol px-6 py-3.5 text-sm font-normal text-white transition-colors hover:bg-petrol-dark"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Prenota una visita
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
